import { fileSystemActions, messageActions } from '../action';
import { fileSystemStore, settingsStore } from '../store';
import {
  lastPathSegment,
  untilLastPathSegment,
  getParentFileSystemDirectory,
} from '../util';
import { WorkerPool } from '../util/worker';
import {
  sendCommandAsync,
  sendCommandAsyncBatch,
  sendCommandToDefault,
} from '../socket';
import {
  RTCStreamSingleton,
  WSConnectionSingleton,
  mainConnection,
} from '../socket';
import * as _ from 'lodash';
let FsWorker = require('worker-loader?name=filesystem.worker.js!./filesystem-worker');

const rS = RTCStreamSingleton.getConnection();
const tC = WSConnectionSingleton.getConnection();
//let fsWorker: Worker = new FsWorker
let fsWorkerPool = new WorkerPool(FsWorker, 1);

/**
 * Filesystem API definitions and socket hooks are here.
 * Exports the api directly as fsApi.
 */

// File uploading
const BYTES_PER_CHUNK = 15 * 1024;
var currentFileUploaded = null;
var currentFile: File = null;
var currentChunk = 0;
let currentId: number[];
var fileReader = new FileReader();
fileReader.onload = async function(e) {
  let data = new Uint8Array(
    currentId.length + 4 + (fileReader.result as ArrayBuffer).byteLength,
  );
  let dataView = new DataView(data.buffer);
  data.set(currentId);
  dataView.setUint32(currentId.length, currentChunk);
  data.set(
    new Uint8Array(fileReader.result as ArrayBuffer),
    currentId.length + 4,
  );

  if (rS.channelOpen) {
    await rS.sendFileStream(data.buffer);
  } else {
    tC.sendRawToServer(data.buffer);
  }

  currentChunk++;

  if (BYTES_PER_CHUNK * currentChunk < currentFile.size) readNextChunk();
};

fileReader.onerror = function(e) {
  console.log('error on file upload', JSON.stringify(e));
};

fileReader.onprogress = function(e) {
  console.log('upload progress', JSON.stringify(e));
  var storeFile = fileSystemStore.uploads[currentFile.name];
  var totalLoaded = BYTES_PER_CHUNK * currentChunk;

  // Only update every 1% change to reduce redraws.
  var shouldUpdate =
    !storeFile || (totalLoaded - storeFile.uploaded) / currentFile.size > 0.01;

  if (shouldUpdate) {
    fileSystemActions.uploadProgress({
      path: currentFile.name,
      uploaded: totalLoaded,
      total: currentFile.size,
    });
  }
};

function readNextChunk() {
  var start = BYTES_PER_CHUNK * currentChunk;
  var end = Math.min(currentFile.size, start + BYTES_PER_CHUNK);
  fileReader.readAsArrayBuffer(currentFile.slice(start, end));
}

// File downloading
interface FileInfo {
  totalSize: number;
  downloadedSize: number;
  data: any[];
}

var downloadingFiles: FileInfo[] = [];

function createAndDownloadBlobFile(body: Uint8Array[], filename: string) {
  const blob = new Blob(body);
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');
    // Browsers that support HTML5 download attribute
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

function endDownload(fileName: string, data: Uint8Array[]) {
  createAndDownloadBlobFile(data, fileName);
}

function downloadingFileChunkHandle(
  location: string,
  msg: any,
  id: number,
  downloadProgress?: (progress: FileProgress) => any,
): boolean {
  const file = downloadingFiles[id];

  if (msg.status === 'begin') {
    file.totalSize = msg.fileSize;
    console.log(`The ${location} file downloading...`);
    return false;
  } else if (msg.status === 'inProcess') {
    file.data.push(msg.binary);
    file.downloadedSize += msg.binary.byteLength;
    if (file.totalSize > 0 && downloadProgress) {
      downloadProgress({
        fileId: id,
        filepath: location,
        percent: file.downloadedSize / file.totalSize,
      });
    }
    return false;
  } else if (msg.status === 'end') {
    endDownload(location.replace(/\\/g, '/').replace(/.*\//, ''), file.data);
    delete downloadingFiles[id];
    downloadProgress({
      fileId: id,
      filepath: location,
      percent: 1,
    });
    return true;
  }
}

export interface FileProgress {
  fileId: number;
  filepath: string;
  percent: number;
}

export const fsApi = {
  createNewFolder(folderName: string, parentDirPath: string) {
    sendCommandAsync(
      'createNewFolder',
      folderName,
      parentDirPath,
      (result: FileSystemActionResult) => {
        if (!result.success) {
          messageActions.message({
            style: 'danger',
            text: result.message,
          });
          return;
        }
        fsApi.reloadFileTree(parentDirPath);
        messageActions.msg('success', result.message);
      },
    );
  },
  deleteFileSystemEntries(targetEntryPaths: string[], parentDir: string) {
    sendCommandAsync(
      'deleteFileSystemEntries',
      targetEntryPaths,
      (result: FileSystemActionResult) => {
        if (result.success) {
          messageActions.msg('success', result.message);
        } else {
          messageActions.msg('danger', result.message);
        }

        // Reload on failure too, because some might have been deleted.
        fsApi.reloadFileTree(parentDir);
      },
    );
  },
  pasteFileSystemEntries(
    targetDirectory: string,
    pasteOperation: 'Copy' | 'Cut',
    targetEntryPaths: string[],
  ) {
    sendCommandAsync(
      'pasteFileSystemEntries',
      targetDirectory,
      pasteOperation,
      targetEntryPaths,
      (result: FileSystemActionResult) => {
        if (result.success) {
          messageActions.msg('success', result.message);
        } else {
          messageActions.msg('danger', result.message);
        }
        fsApi.reloadFileTree(targetDirectory);
      },
    );
  },
  renameFileSystemEntry(targetEntryPath: string, newName: string) {
    sendCommandAsync(
      'renameFileSystemEntry',
      targetEntryPath,
      newName,
      (result: FileSystemActionResult) => {
        if (!result.success) {
          messageActions.msg('danger', result.message);
          return;
        }
        var parentDir = getParentFileSystemDirectory(targetEntryPath);
        fsApi.reloadFileTree(parentDir);
        messageActions.msg('success', result.message);
      },
    );
  },
  requestFile(
    location: string,
    downloadProgress?: (progress: FileProgress) => any,
  ) {
    if (rS.channelOpen) {
      let id = rS.sendAsyncBatch('FileDownloading', { location }, msg => {
        return downloadingFileChunkHandle(location, msg, id, downloadProgress);
      });
      downloadingFiles[id] = { totalSize: -1, downloadedSize: 0, data: [] };
    } else {
      let id = sendCommandAsyncBatch('FileDownloading', { location }, msg => {
        return downloadingFileChunkHandle(location, msg, id, downloadProgress);
      });
      downloadingFiles[id] = { totalSize: -1, downloadedSize: 0, data: [] };
    }
  },

  uploadFile(path: string, file: File, fileUploaded: any = null) {
    currentFileUploaded = fileUploaded;
    currentFile = file;
    currentChunk = 0;
    let header = {
      filePath: path,
      fileSize: file.size,
    };

    if (rS.channelOpen) {
      rS.sendFileHeader(header);
    } else {
      // HACK: send JSON as string to force passing a string type of message
      // FileUploadingPacketHandler is waiting for the string type of header
      tC.sendRawToServer(JSON.stringify(header));
    }
  },

  search(query: string, startingFolder: string) {
    sendCommandAsync(
      'searchFiles',
      query,
      startingFolder,
      (result: SearchResult) => {
        fileSystemActions.search(result);
      },
    );
  },

  reloadFileTree(path: string) {
    sendCommandAsync('createFileTree', path, fileSystemActions.reloadFileTree);
  },

  removeFile(path: string) {
    const name = lastPathSegment(path);
    sendCommandAsync(
      'removeFile',
      settingsStore.settings.WebServer.WebFilePath + 'temp/' + name,
      response => {
        if (response.deleted) {
          fileSystemActions.removeDownload(path);
        }
      },
    );
  },
  createFileTree(path: string = '') {
    if (path == '') {
      sendCommandAsync('requestSystemInformation', (info: SystemInfo) => {
        fileSystemActions.createRoot(info.drives);
      });
    } else {
      sendCommandToDefault('createFileTree', path);
    }
  },
  unzipFile(
    zipFilePath: string,
    destinationFolderName: string,
    parentDir: string,
  ) {
    sendCommandAsync(
      'unzipFile',
      zipFilePath,
      destinationFolderName,
      parentDir,
      (result: FileSystemActionResult) => {
        if (result.success) {
          messageActions.msg('success', result.message);
        } else {
          messageActions.msg('danger', result.message);
        }

        // Reload on failure too, because ZIP might still have been created.
        fsApi.reloadFileTree(parentDir);
      },
    );
  },
  zipFileSystemEntries(
    zipFileName: string,
    entries: string[],
    parentDir: string,
  ) {
    sendCommandAsync(
      'zipFileSystemEntries',
      zipFileName,
      parentDir,
      entries,
      (result: FileSystemActionResult) => {
        if (result.success) {
          messageActions.msg('success', result.message);
        } else {
          messageActions.msg('danger', result.message);
        }

        // Reload on failure too, because ZIP might still have been created.
        fsApi.reloadFileTree(parentDir);
      },
    );
  },
};

function guidToBytes(guid) {
  var bytes = [];
  guid.split('-').map((number, index) => {
    var bytesInChar =
      index < 3 ? number.match(/.{1,2}/g).reverse() : number.match(/.{1,2}/g);
    bytesInChar.map(byte => {
      bytes.push(parseInt(byte, 16));
    });
  });
  return bytes;
}

function isFileUploadingRtc(name: string, message: any) {
  return message.endpoint == 'FileUploading';
}

function FileUploadingHandler(msg: FileSystemInfo.FileUploadResult) {
  if (msg.action == 'start') {
    currentId = guidToBytes(msg.id);
    readNextChunk();
  } else if (msg.action == 'finish') {
    if (currentFileUploaded) currentFileUploaded();
  }
}

rS.listenKeys(isFileUploadingRtc, {
  _(msg: FileSystemInfo.FileUploadResult) {
    FileUploadingHandler(msg);
  },
});

function isFileUploading(message: any) {
  return message.endpoint == 'FileUploading';
}
mainConnection.listenAll([isFileUploading, FileUploadingHandler]);

fsWorkerPool.listen({
  uploadProgress(progress) {
    console.log(progress);
    fileSystemActions.uploadProgress(progress);
  },
  uploadFile({ sent, path }) {
    messageActions.message({ style: 'success', text: 'File uploaded.' });
    fsApi.reloadFileTree(untilLastPathSegment(path));
  },
  requestFile(result) {
    console.log('Download compree.');
    sendCommandAsync('removeFile', [lastPathSegment(result.path)]);
    fileSystemActions.downloadComplete(result);
  },
  progress(content) {
    //console.log(content)
    console.log('We get progress.');
    fileSystemActions.downloadProgress(content);
  },
});

export function createFileTree(tree: FileSystemInfo.FileTree) {
  //console.log(tree)
  fileSystemActions.updateFileTree(tree);
}

export function downloadData(data: FileSystemInfo.Data) {
  fsWorkerPool.post('downloadData', data);
  //fileSystemActions.downloadData(data)
}

export function uploadFile(file: FileSystemInfo.FileUpload) {
  if (file.fileUploaded) {
    messageActions.message({
      style: 'success',
      text: `File ${file.fileName} uploaded to ${file.path}.`,
    });
    sendCommandAsync(
      'createFileTree',
      fileSystemStore.tree.rootFolder.name,
      fileSystemActions.reloadFileTree,
    );
  } else {
    messageActions.message({
      style: 'danger',
      text: `File ${file.fileName} failed to upload.`,
    });
  }
}
